import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Flame, LayoutDashboard, UserCheck } from 'lucide-react';
import type { MemberMe, CheckInResult, CheckInStreak } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Logo, Skeleton, cn } from '@techsofit/ui';
import { MemberLogin } from '../components/MemberLogin';
import { StreakWeekCard } from '../components/StreakWeekCard';
import { getMemberMe, getMyCheckInStatus, selfCheckIn } from '../lib/memberApi';
import { hasGymAccess } from '../lib/membership';
import { quoteOfDay } from '../lib/quotes';

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Drop a stale `?lt=` (one-tap token) if the member was already signed in. */
function stripLinkToken() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('lt')) return;
  url.searchParams.delete('lt');
  window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
}

/**
 * The page the gym's printed QR points at. Members land here, sign in once if needed (the OTP
 * or the email's one-tap button returns them right here — no redirect dance), then tap once to
 * log today's visit.
 */
export function CheckInPage() {
  const [me, setMe] = useState<MemberMe | null | undefined>(undefined);

  useEffect(() => {
    getMemberMe()
      .then((m) => {
        stripLinkToken();
        setMe(m);
      })
      .catch(() => setMe(null));
  }, []);

  if (me === undefined) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
        <Skeleton className="h-64 w-full max-w-sm rounded-card" />
      </div>
    );
  }
  return me ? <CheckInView me={me} /> : <MemberLogin context="checkin" onAuthed={setMe} />;
}

function CheckInView({ me }: { me: MemberMe }) {
  const first = me.fullName.split(/\s+/)[0];
  const canSelfCheckIn = hasGymAccess(me.status);
  const [done, setDone] = useState<CheckInResult | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [streak, setStreak] = useState<CheckInStreak | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(!canSelfCheckIn);

  // Already in today? Then we greet them as checked in rather than offering the button again.
  useEffect(() => {
    getMyCheckInStatus()
      .then((s) => {
        setCheckedAt(s.checkedIn ? s.checkedInAt : null);
        if (s.hasGymAccess === false) setBlocked(true);
        if (s.streak) setStreak(s.streak);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  async function check() {
    setBusy(true);
    setError(null);
    try {
      const result = await selfCheckIn();
      setDone(result);
      if (result.streak) setStreak(result.streak);
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'no_gym_access') {
        setBlocked(true);
        setError(e.message);
      } else {
        setError('Could not check you in. Try again, or ask the front desk.');
      }
    } finally {
      setBusy(false);
    }
  }

  const inAt = done?.checkedInAt ?? checkedAt;
  const isIn = !!inAt;
  const justNow = !!done && !done.alreadyCheckedIn;
  const noAccessCopy =
    me.status === 'NEVER_PAID'
      ? "You don't have gym access yet. Ask the front desk — they can check you in."
      : 'Your gym access has ended. Ask the front desk — they can check you in.';

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
        <Skeleton className="h-64 w-full max-w-sm rounded-card" />
      </div>
    );
  }

  if (isIn) {
    return <CheckedIn me={me} first={first} inAt={inAt!} streak={streak} justNow={justNow} />;
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
      <Card className="w-full max-w-sm p-6">
        <div className="flex items-center justify-between">
          <Logo />
          <span className="text-xs font-medium uppercase tracking-wide text-muted">{me.gymName}</span>
        </div>
        <p className="mt-6 text-sm text-muted">{greeting()}</p>
        <h1 className="font-display text-display-sm text-ink">{first} 👋</h1>

        {blocked ? (
          <p className="mt-4 rounded-control bg-surface px-3.5 py-3 text-sm text-ink-soft">{error ?? noAccessCopy}</p>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted">One tap to log today’s visit.</p>
            <Button block size="lg" className="mt-5" onClick={check} loading={busy}>
              {!busy && <UserCheck className="size-5" aria-hidden />} Check in
            </Button>
            {error && (
              <p role="alert" className="mt-3 text-sm font-medium text-bad-ink">
                {error}
              </p>
            )}
          </>
        )}

        {streak && <StreakWeekCard streak={streak} className="mt-5 bg-surface shadow-none" />}

        <Link
          to="/me"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-ink"
        >
          <LayoutDashboard className="size-4" aria-hidden /> Open my dashboard
        </Link>
      </Card>
    </div>
  );
}

/** The screen members see after they're in — built to feel like a small win, every time. */
function CheckedIn({
  me,
  first,
  inAt,
  streak,
  justNow,
}: {
  me: MemberMe;
  first: string;
  inAt: string;
  streak: CheckInStreak | null;
  justNow: boolean;
}) {
  const navigate = useNavigate();
  const quote = quoteOfDay();
  const days = streak?.daysThisWeek ?? 0;
  const weeks = streak?.weekStreak ?? 0;
  const headline = justNow ? `You’re in, ${first}.` : `Already in today, ${first}.`;
  const sub =
    days >= 5
      ? 'Five days this week — that’s elite consistency.'
      : days >= 3
        ? `Day ${days} this week. The habit is sticking.`
        : days === 2
          ? 'Second session this week. Keep stacking them.'
          : 'First session of the week. Make it count.';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
      <Card className={cn('w-full max-w-sm overflow-hidden p-0', justNow && 'animate-pop-in')}>
        <div className="relative bg-dark px-6 pb-7 pt-6 text-on-dark">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-on-dark-muted">{me.gymName}</span>
            <span className="rounded-pill bg-accent px-2.5 py-1 text-xs font-semibold text-accent-ink tabular">
              {time(inAt)}
            </span>
          </div>
          <span className="mt-6 grid size-14 place-items-center rounded-full bg-accent text-accent-ink">
            <Check className="size-7" strokeWidth={2.5} aria-hidden />
          </span>
          <h1 className="mt-4 font-display text-display-sm leading-tight">{headline}</h1>
          <p className="mt-1.5 text-sm text-on-dark-muted">{sub}</p>

          {weeks >= 2 && (
            <p className="mt-4 inline-flex items-center gap-1.5 rounded-pill bg-dark-soft px-3 py-1.5 text-xs font-semibold">
              <Flame className="size-3.5 text-accent" aria-hidden /> {weeks} weeks in a row
            </p>
          )}
        </div>

        <div className="space-y-4 p-5">
          {streak && <StreakWeekCard streak={streak} className="bg-surface p-4 shadow-none" />}

          <blockquote className="border-l-2 border-accent pl-3">
            <p className="text-sm leading-relaxed text-ink">“{quote.text}”</p>
            <footer className="mt-1 text-xs text-muted">— {quote.author}</footer>
          </blockquote>

          <Button block size="lg" variant="dark" onClick={() => navigate('/me')}>
            <LayoutDashboard className="size-5" aria-hidden /> Open my dashboard <ArrowRight className="size-4" aria-hidden />
          </Button>
          <p className="text-center text-xs text-muted">Have a great session 💪</p>
        </div>
      </Card>
    </div>
  );
}
